import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MapContainer, GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import MapUpdater from './MapUpdater';
import MaskLayer from './MaskLayer';

const GIS = () => {
  const [mapService] = useState("bhuvan");
  const [mapCenter] = useState([20.5937, 78.9629]);
  const [mapZoom] = useState(5);
  const [stateData, setStateData] = useState(null);
  const [stateColors, setStateColors] = useState({});
  const [selectedOption, setSelectedOption] = useState('PDMC');
  const [stateCounts, setStateCounts] = useState(null);
  const [recordCount, setRecordCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRenderedRef = useRef(false);
  const [showTopStates, setShowTopStates] = useState(true);

  const options = [
    'PDMC', 'RAD', 'Agroforestry', 'Seed Distribution', 'NMEOOS', 'NF',
    'NBHM', 'MIDH', 'MOVCD', 'CDB', 'DASD', 'GT', 'Crop Health', 'NMEOOP', 'RKVY'
  ];

  const colorFamilies = [
    "#e53935", "#d32f2f", "#f44336", "#e57373", // red family
    "#ec407a", "#d81b60", "#f06292", "#f8bbd0", // pink family
    "#fb8c00", "#f57c00", "#ffb74d", "#ffe0b2", // orange family
    "#43a047", "#388e3c", "#81c784", "#c8e6c9"  // green family
  ];

  let colorIndex = 0;
  const getNextColor = () => {
    const color = colorFamilies[colorIndex % colorFamilies.length];
    colorIndex++;
    return color;
  };

  useEffect(() => {
    const fetchStateBoundary = async () => {
      try {
        setLoading(true);
        setMapLoaded(false);
        const res = await fetch("/stateboundary.json");
        if (!res.ok) throw new Error("Failed to load state data");
        const json = await res.json();
        setStateData(json);
        mapRenderedRef.current = false;
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchStateBoundary();
  }, []);

  useEffect(() => {
    if (!selectedOption) {
      setStateCounts(null);
      setRecordCount(null);
      return;
    }

    const schemeCodeMap = {
      'PDMC': 1, 'RAD': 2, 'Agroforestry': 3, 'Seed Distribution': 4,
      'NMEOOS': 5, 'NF': 6, 'NBHM': 7, 'MIDH': 8, 'MOVCD': 9,
      'CDB': 10, 'DASD': 11, 'GT': 12, 'Crop Health': 13, 'NMEOOP': 14, 'RKVY': 15
    };

    const schemeCode = schemeCodeMap[selectedOption];

    const fetchData = async () => {
      try {
        setLoading(true);
        setMapLoaded(false);
        mapRenderedRef.current = false;

        const response = await axios.post(
          `${process.env.NODE_ENV === "development"
            ? process.env.REACT_APP_DEV_MODE
            : process.env.REACT_APP_PRO_MODE
          }/api/LatLongPolygonList/LatLongPolygonList`,
          {
            schemeCode,
            finYear: "",
            stateCode: 0,
            districtCode: 0,
            subDistrictCode: 0,
            villageCode: 0
          },
          {
            headers: {
              "Content-Type": "application/json",
              "accept": "/"
            }
          }
        );

        setRecordCount(response.data.recordCount);

        const dataContent = response.data.dataContent;

        const groupedData = dataContent.reduce((acc, record) => {
          const name = record.stateName.trim().toUpperCase();
          acc[name] = (acc[name] || 0) + 1;
          return acc;
        }, {});

        setStateCounts(groupedData);

        const colors = {};
        colorIndex = 0;
        for (const stateName in groupedData) {
          colors[stateName] = getNextColor();
        }
        setStateColors(colors);

      } catch (error) {
        console.error(`Error fetching data for ${selectedOption}:`, error);
        setStateCounts(null);
        setRecordCount(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOption]);

  const handleChange = (e) => {
    setSelectedOption(e.target.value);
  };

  const stateStyle = (feature) => {
    const stateName = feature.properties.STATE.trim().toUpperCase();
    const fillColor = stateColors[stateName] || "#ffffff";
    return {
      color: "#333",
      weight: 1.5,
      fillColor,
      fillOpacity: 0.8,
    };
  };

  const onEachFeature = (feature, layer) => {
    const stateName = feature.properties.STATE.trim().toUpperCase();
    const count = stateCounts ? (stateCounts[stateName] || 0) : 0;
    const stateColor = stateColors[stateName] || '#ccc';

    const tooltipContent = `
      <div class="countno_box">
        <div class="boxCol1" style="background-color: ${stateColor}; color: white; padding: 8px 10px; border-radius: 4px;">
          <h5 style="margin: 0; font-size: 16px;">${count} <span style="font-weight: 400;">Survey</span></h5>
        </div>
        <h6 style="margin-top: 5px;">State : <span>${feature.properties.STATE}</span></h6>
      </div>
    `;

    layer.bindTooltip(tooltipContent, {
      sticky: true,
      direction: 'top',
      className: 'custom-tooltip',
    });

    layer.on({
      mouseover: () => layer.setStyle({ weight: 3, color: "#111" }),
      mouseout: () => layer.setStyle({ weight: 1.5, color: "#333" }),
      click: () => alert(`You clicked on ${feature.properties.STATE}`),
    });

    if (!mapRenderedRef.current) {
      mapRenderedRef.current = true;
      setTimeout(() => setMapLoaded(true), 400);
    }
  };

  const topStates = stateCounts
    ? Object.entries(stateCounts)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
    : [];

  return (
    <div className="row" style={{ backgroundColor: "#ffffff", padding: "10px", border: "1px solid #ccc", minHeight: "100vh" }}>
      <div className="col-md-8">
        <h3>Mission for Integrated Development of Horticulture</h3>
      </div>

      <div className="col-md-4" style={{ marginBottom: 10 }}>
        <select
          id="schemeDropdown"
          value={selectedOption}
          onChange={handleChange}
          className="form-select"
        >
          {options.map((opt, idx) => (
            <option key={idx} value={opt}>{opt}</option>
          ))}
        </select>
      </div>

      {recordCount !== null && (
        <div style={{ marginBottom: 10, fontWeight: 'bold', textAlign: 'right', paddingRight: '18px' }}>
          Total Records: {recordCount}
        </div>
      )}

      <div style={{ display: "flex", gap: "20px" }}>
        <div style={{ flex: 1, position: "relative" }}>
          {(loading || !mapLoaded) && (
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "80vh",
              background: "rgba(255,255,255,0.6)",
              zIndex: 999,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "20px",
              fontWeight: "bold",
              borderRadius: "10px"
            }}>
              Loading...
            </div>
          )}

         

          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            style={{ height: "80vh", width: "100%",  backgroundColor: "#ffffff" }}
            dragging={false}
            doubleClickZoom={false}
            scrollWheelZoom={false}
            touchZoom={false}
            zoomControl={false}
          >
            <MapUpdater center={mapCenter} zoom={mapZoom} />
            <MaskLayer
              selectedState={null}
              selectedDistrict={null}
              stateData={stateData}
              selectedVillage={null}
              mapService={mapService}
            />
            {stateData && (
              <GeoJSON
                key={`${selectedOption}-${JSON.stringify(stateCounts)}`}
                data={stateData}
                style={stateStyle}
                onEachFeature={onEachFeature}
              />
            )}
          </MapContainer>
        </div>

        {/* Top 5 States */}
       {topStates.length > 0 && (
        <div className="homestate_color">
            <h5
            style={{ marginBottom: "12px", display: "inline-flex", alignItems: "center", cursor: "pointer" }}
            onClick={() => setShowTopStates(!showTopStates)}
            >
            Top 5 States by Count
            <span style={{ marginLeft: "6px", fontSize: "0.8em", userSelect: "none" }}>
                {showTopStates ? '▲' : '▼'}
            </span>
            </h5>

            {showTopStates && (
            <ol style={{ paddingLeft: "10px", margin: 0 }}>
                {topStates.map(([state, count], index) => (
                <li key={index} style={{ marginBottom: "5px", display: "flex", alignItems: "center" }}>
                    <span style={{
                    display: "inline-block",
                    width: "13px",
                    height: "13px",
                    backgroundColor: stateColors[state] || "#000",
                    marginRight: "5px",
                    borderRadius: "3px"
                    }}></span>
                    <span><strong>{state}</strong>: {count}</span>
                </li>
                ))}
            </ol>
            )}
        </div>
        )}
      </div>
    </div>
  );
};

export default GIS;
